/**
 * PreloadManager — Phase-aware asset pre-fetching with progress tracking.
 *
 * Warms the AssetCache by downloading assets in priority order before
 * they are needed at runtime. Integrators declare what to preload,
 * the manager handles ordering, concurrency, progress, and events.
 *
 * @see docs/ims-engine-api/14-feature-preload-registry.md
 */

import type { AssetLoader } from './AssetLoader';
import type { AssetCache } from './AssetCache';
import type {
  PreloadEntry,
  PreloadAssetType,
  PreloadPresetConfig,
  PreloadProgress,
  PreloadCategoryProgress,
  PreloadEntryStatus,
  PreloadEntryState,
} from '../api/types/preload';
import type { LoadingPhase } from '../api/types/loading';
import { resolvePresetEntries } from './resolvePresetEntries';

// ---------------------------------------------------------------------------
// Internal entry state
// ---------------------------------------------------------------------------

interface InternalEntry {
  id: string;
  url: string;
  type: PreloadAssetType;
  category: string;
  phase: LoadingPhase;
  status: PreloadEntryStatus;
  error?: string;
}

// ---------------------------------------------------------------------------
// Phase priority (lower = higher priority)
// ---------------------------------------------------------------------------

const PHASE_ORDER: Record<LoadingPhase, number> = {
  critical: 0,
  eager: 1,
  lazy: 2,
  idle: 3,
};

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface PreloadManagerOptions {
  /** Called on every progress update. */
  onProgress?: (progress: PreloadProgress) => void;
  /** Called when all entries have finished (loaded or errored). */
  onComplete?: () => void;
  /** Maximum concurrent downloads. Default: 3 */
  concurrency?: number;
  /** Base URL for HDR environment files. Used by preloadPreset(). Default: '/assets/hdri' */
  hdriBasePath?: string;
}

// ---------------------------------------------------------------------------
// PreloadManager
// ---------------------------------------------------------------------------

export class PreloadManager {
  private _loader: AssetLoader;
  private _cache: AssetCache;
  private _entries = new Map<string, InternalEntry>();
  private _onProgress: ((progress: PreloadProgress) => void) | null;
  private _onComplete: (() => void) | null;
  private _concurrency: number;
  private _hdriBasePath: string;
  private _disposed = false;

  /** Number of currently in-flight downloads. */
  private _inflight = 0;

  /** Queue of entry IDs sorted by phase priority. */
  private _queue: string[] = [];



  constructor(
    loader: AssetLoader,
    cache: AssetCache,
    options?: PreloadManagerOptions,
  ) {
    this._loader = loader;
    this._cache = cache;
    this._onProgress = options?.onProgress ?? null;
    this._onComplete = options?.onComplete ?? null;
    this._concurrency = options?.concurrency ?? 3;
    this._hdriBasePath = options?.hdriBasePath ?? '/assets/hdri';
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Register a batch of assets for preloading.
   * Idempotent — calling with the same URL again is a no-op for that entry.
   * Entries are queued by phase priority and downloading starts immediately.
   */
  preload(entries: PreloadEntry[]): void {
    if (this._disposed) return;

    let added = false;
    for (const entry of entries) {
      // Skip duplicates (by ID)
      if (this._entries.has(entry.id)) continue;

      // Skip assets already in cache
      if (this._cache.has(entry.url)) {
        this._entries.set(entry.id, {
          id: entry.id,
          url: entry.url,
          type: entry.type ?? 'glb',
          category: entry.category ?? 'default',
          phase: entry.phase ?? 'lazy',
          status: 'loaded',
        });
        added = true;
        continue;
      }

      this._entries.set(entry.id, {
        id: entry.id,
        url: entry.url,
        type: entry.type ?? 'glb',
        category: entry.category ?? 'default',
        phase: entry.phase ?? 'lazy',
        status: 'queued',
      });
      this._queue.push(entry.id);
      added = true;
    }

    if (added) {
      // Sort queue by phase priority
      this._sortQueue();
      this._emitProgress();
      this._drainQueue();
    }
  }

  /**
   * Preset-aware preloading — resolves environment IDs and HDR names
   * into concrete entries, deduplicates by URL, and queues them.
   *
   * @example
   * manager.preloadPreset({ environments: ['studio', 'road'] });
   * manager.preloadPreset({ hdris: ['city.hdr', 'forest.hdr'] });
   */
  preloadPreset(config: PreloadPresetConfig): void {
    if (this._disposed) return;
    const entries = resolvePresetEntries(config, this._hdriBasePath);
    if (entries.length > 0) {
      this.preload(entries);
    }
  }

  /** Get the current preload progress snapshot. */
  getProgress(): PreloadProgress {
    return this._buildProgress();
  }

  /** Get the state of a single preload entry. */
  getEntryState(id: string): PreloadEntryState | undefined {
    const e = this._entries.get(id);
    if (!e) return undefined;
    return { ...e };
  }

  /** Cancel all pending preloads and clear state. */
  cancel(): void {
    // Clear the queue — in-flight loads will still complete
    this._queue = [];
    // Mark all queued entries as cancelled... just remove them
    for (const [id, entry] of this._entries) {
      if (entry.status === 'queued') {
        this._entries.delete(id);
      }
    }
    this._emitProgress();
  }

  /** Dispose the manager. Does NOT abort in-flight loads (that's AssetLoader's job). */
  dispose(): void {
    this._disposed = true;
    this._queue = [];
    this._entries.clear();
    this._onProgress = null;
    this._onComplete = null;
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  /** Sort the queue by phase priority (critical first, idle last). */
  private _sortQueue(): void {
    this._queue.sort((a, b) => {
      const ea = this._entries.get(a)!;
      const eb = this._entries.get(b)!;
      return PHASE_ORDER[ea.phase] - PHASE_ORDER[eb.phase];
    });
  }

  /** Start loading entries up to concurrency limit. */
  private _drainQueue(): void {
    if (this._disposed) return;

    while (this._inflight < this._concurrency && this._queue.length > 0) {
      const id = this._queue.shift()!;
      const entry = this._entries.get(id);
      if (!entry || entry.status !== 'queued') continue;

      entry.status = 'loading';
      this._inflight++;
      this._loadEntry(entry);
    }
  }

  /** Load a single entry via the correct loader method. */
  private async _loadEntry(entry: InternalEntry): Promise<void> {
    try {
      const opts = { phase: entry.phase };
      switch (entry.type) {
        case 'hdr':
          await this._loader.loadHDR(entry.url, opts);
          break;
        case 'texture':
          await this._loader.loadTexture(entry.url, opts);
          break;
        case 'glb':
        default:
          await this._loader.loadGLB(entry.url, opts);
          break;
      }
      if (this._disposed) return;
      entry.status = 'loaded';
    } catch (err) {
      if (this._disposed) return;
      entry.status = 'error';
      entry.error = err instanceof Error ? err.message : String(err);
    } finally {
      this._inflight--;
    }

    this._emitProgress();
    this._checkComplete();
    this._drainQueue();
  }

  /** Emit progress via callback. */
  private _emitProgress(): void {
    if (this._disposed || !this._onProgress) return;
    this._onProgress(this._buildProgress());
  }

  /** Check if all entries are done and fire onComplete. */
  private _checkComplete(): void {
    if (this._disposed) return;

    for (const entry of this._entries.values()) {
      if (entry.status === 'queued' || entry.status === 'loading') return;
    }

    // All entries are terminal (loaded or error)
    this._onComplete?.();
  }

  /** Build the progress snapshot. */
  private _buildProgress(): PreloadProgress {
    const categories: Record<string, PreloadCategoryProgress> = {};
    const pending: string[] = [];
    const loaded: string[] = [];
    const failed: string[] = [];

    for (const entry of this._entries.values()) {
      // Aggregate per-category
      if (!categories[entry.category]) {
        categories[entry.category] = { loaded: 0, total: 0, percentage: 0 };
      }
      const cat = categories[entry.category];
      cat.total++;

      switch (entry.status) {
        case 'loaded':
          cat.loaded++;
          loaded.push(entry.id);
          break;
        case 'error':
          failed.push(entry.id);
          break;
        case 'queued':
        case 'loading':
          pending.push(entry.id);
          break;
      }
    }

    // Compute percentages
    for (const cat of Object.values(categories)) {
      cat.percentage = cat.total > 0 ? cat.loaded / cat.total : 0;
    }

    const totalEntries = this._entries.size;
    const totalLoaded = loaded.length;

    return {
      total: totalEntries > 0 ? totalLoaded / totalEntries : 0,
      categories,
      pending,
      loaded,
      failed,
    };
  }
}
